import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';
import { LayoutDef } from '../admin/nyomtatvany/nyomtatvany.component';

interface Mezo {
  eazon: string;
  ertek: string;
  label?: string;
  lap: string;
  tipuskod: string;
}

interface LayoutMezoMeta {
  tipus: string;
  dropdownStatikus?: { ertek: string; nev: string }[];
  kotelezo?: boolean;
  minOccurs?: number | string;
  maxOccurs?: number | string;
  ismetelheto?: boolean;
  mintazat?: string;
  maxHossz?: number;
  minHossz?: number;
  format?: string;
  sugo?: string;
  szamitasTipus?: 'nincs' | 'masol' | 'keplet';
  forrasEazon?: string;
  keplet?: string;
  readonly?: boolean;
}

/** One <nyomtatvany> block (A lap, or one M instance) */
interface LapInstancia {
  azonosito: string;  // e.g. '2608A', '2608M'
  lapNev: string;     // e.g. '2608A', '2608M #1'
  mezok: Mezo[];
  infoBlock: string;  // raw XML content of <nyomtatvanyinformacio>…</nyomtatvanyinformacio>
  ujHozzaadott: boolean;
  oldalak: string[];  // page names within this instance (computed after layout load)
}

interface FajlSablon {
  fajlnev: string;
  azonosito: string;
  verzio: string;
  adozoNev: string;
}

type RenderTipus = 'checkbox' | 'boxed' | 'text';

interface DropdownOption {
  value: string;
  label: string;
}

interface ViewMezo extends Mezo { szeles: number; }
interface ViewSor { mezok: ViewMezo[]; }
interface ViewBlokk { cim: string; sorok: ViewSor[]; }

@Component({
  selector: 'app-nyomtatvany',
  templateUrl: './nyomtatvany.component.html',
  styleUrls: ['./nyomtatvany.component.scss'],
})
export class NyomtatvanyComponent implements OnInit {

  fajlSablonok: FajlSablon[] = [];
  kivalasztott: FajlSablon | null = null;
  mezok: Mezo[] = [];
  templateXml = '';
  labels: { [eazon: string]: string } = {};
  lapok: string[] = [];
  aktivLap = '';
  lapInstanciak: LapInstancia[] = [];
  kotegeltAzonosito: string | null = null; // e.g. '2608M' — the repeatable lap type
  kotegeltSablonEazonok: string[] = [];
  kotegeltInfoSablon = '';
  viewBlokkjai: ViewBlokk[] = [];
  viewMezokNincsLayout: Mezo[] = [];
  private _aktivOldalCim = '';
  get aktivOldalCim() { return this._aktivOldalCim; }
  set aktivOldalCim(v: string) { this._aktivOldalCim = v; this.berekelViewBlokkjai(); }
  // Layout-based type/dropdown overrides (from saved admin layout, e.g. ENYK import)
  private layoutMezoMap: { [eazon: string]: LayoutMezoMeta } = {};
  @ViewChild('xmlFileInput') xmlFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('enykFileInput') enykFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('txtFileInput') txtFileInputRef!: ElementRef<HTMLInputElement>;
  private layoutOldalakData: any[] = [];
  private layoutEazonOldalMap: { [eazon: string]: string } = {};
  private layoutInstanciaOldalak: { [azonosito: string]: string[] } = {};

  private readonly renderOverrideok: { [formAzonosito: string]: { pattern: RegExp; tipus: RenderTipus; kockaDb?: number }[] } = {
    // 08-as nyomtatvanyoknal jellemzoen tobb kodolt mezo jelenik meg karakterdobozokban.
    '08': [
      { pattern: /^M[0-9]{2}H/, tipus: 'boxed', kockaDb: 8 },
      { pattern: /^M[0-9]{2}F/, tipus: 'checkbox' },
    ],
    '24T34': [
      { pattern: /^M02HC/, tipus: 'boxed', kockaDb: 6 },
      { pattern: /^M02FA/, tipus: 'checkbox' },
    ],
    'TEL': [
      { pattern: /^M01FA/, tipus: 'checkbox' },
      { pattern: /^M01H/, tipus: 'boxed', kockaDb: 8 },
    ],
  };

  private readonly dropdownOverrideok: { [formAzonosito: string]: { pattern: RegExp; opciok: DropdownOption[] }[] } = {
    'TEL': [
      {
        pattern: /^M01FA00AA00$/,
        opciok: [
          { value: '', label: 'Nincs megadva' },
          { value: 'X', label: 'Igen' },
        ],
      },
    ],
    '24T34': [
      {
        pattern: /^M02FA/,
        opciok: [
          { value: '', label: 'Nem' },
          { value: 'X', label: 'Igen' },
        ],
      },
    ],
  };

  private readonly altalanosDropdownok: { pattern: RegExp; opciok: DropdownOption[] }[] = [
    {
      pattern: /orszag|ország/i,
      opciok: [
        { value: '', label: 'Válasszon országot' },
        { value: 'HU', label: 'Magyarország' },
        { value: 'AT', label: 'Ausztria' },
        { value: 'DE', label: 'Németország' },
        { value: 'SK', label: 'Szlovákia' },
        { value: 'RO', label: 'Románia' },
      ],
    },
  ];

  constructor(private http: HttpClient, private data: DataService) {}

  ngOnInit() {
    this.betolt();
  }

  betolt() {
    this.http.get<FajlSablon[]>('/api/nyomtatvany/fajlok').subscribe({
      next: r => this.fajlSablonok = r,
      error: e => this.data.sendToast('Betöltési hiba: ' + (e?.status ? `HTTP ${e.status}` : e?.message), 'error'),
    });
  }

  kivalaszt(s: FajlSablon) {
    this.http.get<{fajlnev: string, xml: string, labels?: {mezok: {[k:string]:string}}}>(`/api/nyomtatvany/fajlok/${s.fajlnev}`).subscribe({
      next: r => {
        this.kivalasztott = s;
        this.templateXml = r.xml;
        this.labels = r.labels?.mezok || {};
        this.aktivOldalCim = '';
        this.layoutMezoMap = {};
        this.layoutEazonOldalMap = {};
        this.layoutInstanciaOldalak = {};
        this.lapInstanciak = this.parseLapInstanciak(r.xml);
        // Start with blank slate — values are loaded explicitly via import buttons
        this.lapInstanciak.forEach(inst => inst.mezok.forEach(m => m.ertek = ''));
        this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
        this.lapok = this.lapInstanciak.map(l => l.lapNev);
        this.aktivLap = this.lapok[0] || '';

        // Detect repeatable (kötegelt) lap type
        // Primary: any azonosito that appears more than once in the XML
        // Fallback: ANyK convention — forms ending in 'M' (melléklet) are always repeatable
        const counts: { [k: string]: number } = {};
        this.lapInstanciak.forEach(l => counts[l.azonosito] = (counts[l.azonosito] || 0) + 1);
        let repeatable = Object.entries(counts).find(([, v]) => v > 1)?.[0] ?? null;
        if (!repeatable && this.lapInstanciak.length > 1) {
          repeatable = this.lapInstanciak.map(l => l.azonosito).find(a => /M\d*$/.test(a)) ?? null;
        }
        this.kotegeltAzonosito = repeatable;
        if (repeatable) {
          const first = this.lapInstanciak.find(l => l.azonosito === repeatable)!;
          this.kotegeltSablonEazonok = first.mezok.map(m => m.eazon);
          this.kotegeltInfoSablon = first.infoBlock;
        }

        // Load saved layout; if empty fall back to auto-generated XSD layout
        this.http.get<LayoutDef>(`/api/nyomtatvany/layout-konfig/${s.fajlnev}`).subscribe({
          next: layout => {
            const allOldalak = layout?.oldalak?.length ? layout.oldalak : ((layout as any)?.blokkok?.length ? [{ cim: 'Főlap', blokkok: (layout as any).blokkok }] : []);
            if (allOldalak.length === 0) {
              this.betoltEnykLayout(s);
            } else {
              this.applyLayoutDef(allOldalak, s);
            }
          },
          error: () => this.betoltEnykLayout(s),
        });
      },
      error: e => this.data.sendToast('Hiba: ' + (e?.status ? `HTTP ${e.status}` : e?.message), 'error'),
    });
  }

  stripElotagok(s: string): string {
    return s.replace(/^(NAV|APEH)_/i, '');
  }

  private betoltEnykLayout(s: FajlSablon) {
    this.http.get<LayoutDef>(`/api/nyomtatvany/xsd-import/${s.fajlnev}`).subscribe({
      next: enykLayout => this.applyLayoutDef(enykLayout?.oldalak || [], s),
      error: () => this.applyLayoutDef([], s),
    });
  }

  private applyLayoutDef(allOldalak: any[], s: FajlSablon) {
    this.layoutMezoMap = {};
    this.layoutEazonOldalMap = {};
    this.layoutInstanciaOldalak = {};
    allOldalak.forEach((o: any) => {
      const fId: string | undefined = o.formId;
      if (fId) {
        if (!this.layoutInstanciaOldalak[fId]) this.layoutInstanciaOldalak[fId] = [];
        if (!this.layoutInstanciaOldalak[fId].includes(o.cim)) {
          this.layoutInstanciaOldalak[fId].push(o.cim);
        }
      }
      (o.blokkok || []).forEach((b: any) => (b.sorok || []).forEach((sor: any) => (sor.mezok || []).forEach((m: any) => {
        this.layoutMezoMap[m.eazon] = {
          tipus: m.tipus,
          dropdownStatikus: m.dropdownStatikus,
          kotelezo: m.kotelezo,
          minOccurs: m.minOccurs,
          maxOccurs: m.maxOccurs,
          ismetelheto: m.ismetelheto,
          mintazat: m.mintazat,
          maxHossz: m.maxHossz,
          minHossz: m.minHossz,
          format: m.format,
          sugo: m.sugo,
          szamitasTipus: m.szamitasTipus,
          forrasEazon: m.forrasEazon,
          keplet: m.keplet,
          readonly: m.readonly,
        };
        this.layoutEazonOldalMap[m.eazon] = o.cim;
      })));
    });
    this.lapInstanciak.forEach(inst => {
      const formPages = this.layoutInstanciaOldalak[inst.azonosito];
      if (formPages?.length) {
        inst.oldalak = formPages;
      } else {
        const seen = new Set<string>();
        const result: string[] = [];
        for (const m of inst.mezok) {
          const cim = this.layoutEazonOldalMap[m.eazon];
          if (cim && !seen.has(cim)) { seen.add(cim); result.push(cim); }
        }
        inst.oldalak = result;
      }
    });
    this.layoutOldalakData = allOldalak;
    this.aktivOldalCim = this.lapInstanciak.find(l => l.lapNev === this.aktivLap)?.oldalak?.[0] ?? '';
    this.frissitSzamitottMezok();
  }

  lapHozzaad() {
    if (!this.kotegeltAzonosito) return;
    const existingCount = this.lapInstanciak.filter(l => l.azonosito === this.kotegeltAzonosito).length;
    const lapNev = `${this.kotegeltAzonosito} #${existingCount + 1}`;
    const mezok: Mezo[] = this.kotegeltSablonEazonok.map(eazon => ({
      eazon,
      ertek: '',
      label: this.labels[eazon],
      lap: lapNev,
      tipuskod: this.getTipusKod(eazon),
    }));
    const templateInst = this.lapInstanciak.find(l => l.azonosito === this.kotegeltAzonosito);
    const ujOldalak = templateInst?.oldalak ? [...templateInst.oldalak] : [];
    this.lapInstanciak.push({ azonosito: this.kotegeltAzonosito, lapNev, mezok, infoBlock: this.kotegeltInfoSablon, ujHozzaadott: true, oldalak: ujOldalak });
    this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
    this.lapok = this.lapInstanciak.map(l => l.lapNev);
    this.aktivLap = lapNev;
    this.aktivOldalCim = ujOldalak[0] ?? '';
  }

  lapTorol(lapNev: string) {
    const inst = this.lapInstanciak.find(l => l.lapNev === lapNev);
    if (!inst) return;
    if (this.lapInstanciak.filter(l => l.azonosito === inst.azonosito).length <= 1) return;
    if (!confirm(`"${lapNev}" törlése?`)) return;
    this.lapInstanciak = this.lapInstanciak.filter(l => l.lapNev !== lapNev);
    // Renumber remaining instances of same azonosito
    let num = 0;
    this.lapInstanciak.forEach(l => {
      if (l.azonosito === inst.azonosito) {
        num++;
        l.lapNev = `${inst.azonosito} #${num}`;
        l.mezok.forEach(m => m.lap = l.lapNev);
      }
    });
    this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
    this.lapok = this.lapInstanciak.map(l => l.lapNev);
    if (this.aktivLap === lapNev) {
      this.aktivLap = this.lapok[0] || '';
      this.aktivOldalCim = this.lapInstanciak.find(l => l.lapNev === this.aktivLap)?.oldalak?.[0] ?? '';
    }
  }

  instanciaValtott(lapNev: string) {
    this.aktivLap = lapNev;
    this.aktivOldalCim = this.lapInstanciak.find(l => l.lapNev === lapNev)?.oldalak?.[0] ?? '';
  }

  vissza() {
    this.kivalasztott = null;
    this.mezok = [];
    this.templateXml = '';
    this.lapok = [];
    this.aktivLap = '';
    this._aktivOldalCim = '';
    this.lapInstanciak = [];
    this.kotegeltAzonosito = null;
    this.layoutMezoMap = {};
    this.layoutEazonOldalMap = {};
    this.layoutInstanciaOldalak = {};
    this.layoutOldalakData = [];
    this.viewBlokkjai = [];
    this.viewMezokNincsLayout = [];
  }

  parseLapInstanciak(xml: string): LapInstancia[] {
    // Pass 1: collect raw blocks per <nyomtatvany>...</nyomtatvany>
    const raw: { azonosito: string; infoBlock: string; mezok: Mezo[] }[] = [];
    const blockRegex = /<nyomtatvany>([\s\S]*?)<\/nyomtatvany>/g;
    const azonRegex = /<nyomtatvanyazonosito>([^<]+)<\/nyomtatvanyazonosito>/;
    const infoRegex = /<nyomtatvanyinformacio>([\s\S]*?)<\/nyomtatvanyinformacio>/;
    const mezoRegex = /<mezo eazon="([^"]+)">([^<]*)<\/mezo>/g;

    let blockMatch: RegExpExecArray | null;
    while ((blockMatch = blockRegex.exec(xml)) !== null) {
      const block = blockMatch[1];
      const azonMatch = azonRegex.exec(block);
      const infoMatch = infoRegex.exec(block);
      const azon = azonMatch ? azonMatch[1].trim() : 'Ismeretlen';
      const infoBlock = infoMatch ? infoMatch[1] : '';
      const mezok: Mezo[] = [];
      let mezoMatch: RegExpExecArray | null;
      mezoRegex.lastIndex = 0;
      while ((mezoMatch = mezoRegex.exec(block)) !== null) {
        const eazon = mezoMatch[1];
        mezok.push({ eazon, ertek: mezoMatch[2], label: this.labels[eazon], lap: '', tipuskod: this.getTipusKod(eazon) });
      }
      raw.push({ azonosito: azon, infoBlock, mezok });
    }

    // Fallback for flat XMLs with no <nyomtatvany> structure
    if (raw.length === 0) {
      const mezok: Mezo[] = [];
      mezoRegex.lastIndex = 0;
      let fm: RegExpExecArray | null;
      while ((fm = mezoRegex.exec(xml)) !== null) {
        mezok.push({ eazon: fm[1], ertek: fm[2], label: this.labels[fm[1]], lap: 'Nyomtatvány', tipuskod: this.getTipusKod(fm[1]) });
      }
      if (mezok.length) {
        raw.push({ azonosito: 'Nyomtatvány', infoBlock: '', mezok });
      }
    }

    // Pass 2: assign lap names (number if multiple instances of same azonosito)
    const counts: { [k: string]: number } = {};
    raw.forEach(b => counts[b.azonosito] = (counts[b.azonosito] || 0) + 1);
    const instNum: { [k: string]: number } = {};
    return raw.map(b => {
      instNum[b.azonosito] = (instNum[b.azonosito] || 0) + 1;
      const lapNev = counts[b.azonosito] > 1 ? `${b.azonosito} #${instNum[b.azonosito]}` : b.azonosito;
      b.mezok.forEach(m => m.lap = lapNev);
      return { azonosito: b.azonosito, lapNev, mezok: b.mezok, infoBlock: b.infoBlock, ujHozzaadott: false, oldalak: [] };
    });
  }

  importXmlTrigger() {
    this.xmlFileInputRef.nativeElement.click();
  }

  importEnykTrigger() {
    this.enykFileInputRef.nativeElement.click();
  }

  importTxtTrigger() {
    this.txtFileInputRef.nativeElement.click();
  }

  importXml(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const xml = (e.target?.result as string) || '';
      const imported = this.parseLapInstanciak(xml);
      let merged = 0;
      imported.forEach(importedInst => {
        const target = this.lapInstanciak.find(l => l.azonosito === importedInst.azonosito);
        if (target) {
          const valueMap: { [eazon: string]: string } = {};
          importedInst.mezok.forEach(m => { valueMap[m.eazon] = m.ertek; });
          target.mezok.forEach(m => {
            if (valueMap[m.eazon] !== undefined) { m.ertek = valueMap[m.eazon]; merged++; }
          });
        }
      });
      this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
      this.data.sendToast(`${merged} mező betöltve XML-ből.`, 'success');
      (event.target as HTMLInputElement).value = '';
    };
    reader.onerror = () => this.data.sendToast('Hiba a fájl olvasásakor.', 'error');
    reader.readAsText(file, 'utf-8');
  }

  importEnyk(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const raw = e.target?.result as ArrayBuffer;
      // .enyk is windows-1250 XML; decode with TextDecoder
      let xmlStr: string;
      try {
        xmlStr = new TextDecoder('windows-1250').decode(raw);
      } catch {
        xmlStr = new TextDecoder('utf-8').decode(raw);
      }
      // Extract field values from GUIITEM data elements:
      // <GUIITEM type="data" fid="7559" ... value="24556763113" .../>  (filled form)
      // Also handles default_value attribute
      const valueMap: { [eazon: string]: string } = {};
      const guiitemRegex = /<GUIITEM[^>]+type=["']data["'][^>]*>/gi;
      let match: RegExpExecArray | null;
      while ((match = guiitemRegex.exec(xmlStr)) !== null) {
        const tag = match[0];
        const fidM = tag.match(/\bfid=["']([^"']+)["']/i);
        const valM = tag.match(/\bvalue=["']([^"']*)["']/i) || tag.match(/\bdefault_value=["']([^"']*)["']/i);
        if (fidM && valM) {
          valueMap[fidM[1]] = valM[1];
        }
      }
      let merged = 0;
      this.lapInstanciak.forEach(inst => inst.mezok.forEach(m => {
        if (valueMap[m.eazon] !== undefined) { m.ertek = valueMap[m.eazon]; merged++; }
      }));
      this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
      this.data.sendToast(
        merged ? `${merged} mező betöltve ENYK fájlból.` : 'Az ENYK fájlban nem találhatók kitöltött mezőertékek.',
        merged ? 'success' : 'warning',
      );
      (event.target as HTMLInputElement).value = '';
    };
    reader.onerror = () => this.data.sendToast('Hiba a fájl olvasásakor.', 'error');
    reader.readAsArrayBuffer(file);
  }

  importTxt(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string) || '';
      // Format: lines starting with '$' are metadata; 'eazon=value' lines are field data
      // Support multiple $ny_azon= blocks in the same file
      let currentAzon: string | null = null;
      // Build value map per azonosito: { '2608A': { '7559': '...', ... }, ... }
      const valuesByAzon: { [azon: string]: { [eazon: string]: string } } = {};
      for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line) continue;
        if (line.startsWith('$ny_azon=')) {
          currentAzon = line.split('=', 2)[1].trim();
          if (!valuesByAzon[currentAzon]) valuesByAzon[currentAzon] = {};
          continue;
        }
        if (line.startsWith('$')) continue; // skip other metadata
        const eq = line.indexOf('=');
        if (eq === -1) continue;
        const eazon = line.substring(0, eq).trim();
        const val = line.substring(eq + 1); // value (may be empty)
        if (currentAzon) {
          valuesByAzon[currentAzon][eazon] = val;
        }
      }
      let merged = 0;
      this.lapInstanciak.forEach(inst => {
        const map = valuesByAzon[inst.azonosito];
        if (!map) return;
        inst.mezok.forEach(m => {
          if (map[m.eazon] !== undefined) { m.ertek = map[m.eazon]; merged++; }
        });
      });
      this.mezok = this.lapInstanciak.flatMap(l => l.mezok);
      this.data.sendToast(`${merged} mező betöltve TXT fájlból.`, merged ? 'success' : 'warning');
      (event.target as HTMLInputElement).value = '';
    };
    reader.onerror = () => this.data.sendToast('Hiba a fájl olvasásakor.', 'error');
    reader.readAsText(file, 'utf-8');
  }

  parseMezok(xml: string): Mezo[] {
    return this.parseLapInstanciak(xml).flatMap(l => l.mezok);
  }

  get oldalakAktivInstancian(): string[] {
    return this.lapInstanciak.find(l => l.lapNev === this.aktivLap)?.oldalak ?? [];
  }

  private berekelViewBlokkjai() {
    const inst = this.lapInstanciak.find(l => l.lapNev === this.aktivLap);
    if (!inst || !this.layoutOldalakData.length) {
      this.viewBlokkjai = [];
      this.viewMezokNincsLayout = inst ? inst.mezok : [];
      return;
    }
    const mezoMap = new Map<string, Mezo>(inst.mezok.map(m => [m.eazon, m]));
    const hasPages = inst.oldalak.length > 1;
    const relevantOldalak = this.layoutOldalakData.filter((o: any) => {
      const formMatch = !o.formId || o.formId === inst.azonosito;
      if (!formMatch) return false;
      if (hasPages && this._aktivOldalCim) return o.cim === this._aktivOldalCim;
      return true;
    });
    const result: ViewBlokk[] = [];
    const layoutEazonok = new Set<string>();
    for (const oldal of relevantOldalak) {
      for (const blokk of (oldal.blokkok || [])) {
        const sorok: ViewSor[] = [];
        for (const sor of (blokk.sorok || [])) {
          const mezok: ViewMezo[] = (sor.mezok || [])
            .map((lm: any) => {
              layoutEazonok.add(lm.eazon);
              const m = mezoMap.get(lm.eazon);
              return m ? { ...m, szeles: lm.szeles || 1 } : null;
            })
            .filter((x): x is ViewMezo => x !== null);
          if (mezok.length) sorok.push({ mezok });
        }
        if (sorok.length) result.push({ cim: blokk.cim, sorok });
      }
    }
    this.viewBlokkjai = result;
    // Fields not in any layout block
    const allLayoutEazonok = new Set<string>();
    this.layoutOldalakData.forEach((o: any) =>
      (o.blokkok || []).forEach((b: any) =>
        (b.sorok || []).forEach((sor: any) =>
          (sor.mezok || []).forEach((m: any) => allLayoutEazonok.add(m.eazon))
        )
      )
    );
    this.viewMezokNincsLayout = allLayoutEazonok.size
      ? inst.mezok.filter(m => !allLayoutEazonok.has(m.eazon))
      : inst.mezok;
  }

  get mezokAktivLapon(): Mezo[] {
    const inst = this.lapInstanciak.find(l => l.lapNev === this.aktivLap);
    if (!inst) return this.mezok;
    if (!this.aktivOldalCim || !this.oldalakAktivInstancian.length) return inst.mezok;
    return inst.mezok.filter(m => this.layoutEazonOldalMap[m.eazon] === this.aktivOldalCim);
  }

  isCheckbox(m: Mezo): boolean {
    return this.getRenderTipus(m) === 'checkbox';
  }

  isKockas(m: Mezo): boolean {
    if (this.getRenderTipus(m) !== 'boxed') return false;
    const val = (m.ertek || '').trim();
    if (!val) return true;
    return val.length <= 20 && /^[0-9A-Za-z]+$/.test(val);
  }

  kockaDb(m: Mezo): number {
    const override = this.getOverrideSzabaly(m);
    if (override?.kockaDb) return override.kockaDb;

    const val = (m.ertek || '').trim();
    if (val.length >= 1 && val.length <= 20) return val.length;
    if (this.isDatumMezo(m)) return 8;
    if (this.isAdoazonositoMezo(m)) return 10;
    return 8;
  }

  kockaErtekek(m: Mezo): string[] {
    const n = this.kockaDb(m);
    const chars = (m.ertek || '').split('');
    return Array.from({ length: n }, (_, i) => chars[i] || '');
  }

  kockaValtozott(m: Mezo, idx: number, value: string) {
    const n = this.kockaDb(m);
    const current = Array.from({ length: n }, (_, i) => (m.ertek || '')[i] || '');
    current[idx] = (value || '').replace(/\s+/g, '').slice(-1).toUpperCase();
    m.ertek = current.join('').replace(/\s+$/g, '');
    this.mezoValtozott();
  }

  checkboxValtozott(m: Mezo, checked: boolean | null | undefined) {
    m.ertek = checked ? 'X' : '';
    this.mezoValtozott();
  }

  mezoValtozott() {
    this.frissitSzamitottMezok();
  }

  mezotipusSzoveg(m: Mezo): string {
    const tipus = this.getRenderTipus(m);
    if (tipus === 'checkbox') return 'Jelolodoboz';
    if (tipus === 'boxed') return 'Kockas mezo';
    return 'Szoveges mezo';
  }

  mezotipusClass(m: Mezo): string {
    const tipus = this.getRenderTipus(m);
    if (tipus === 'checkbox') return 'tipus-checkbox';
    if (tipus === 'boxed') return 'tipus-boxed';
    return 'tipus-text';
  }

  isKotelezo(m: Mezo): boolean {
    return this.layoutMezoMap[m.eazon]?.kotelezo === true;
  }

  isReadonly(m: Mezo): boolean {
    const meta = this.layoutMezoMap[m.eazon];
    if (!meta) return false;
    if (meta.readonly) return true;
    return meta.szamitasTipus === 'masol' || meta.szamitasTipus === 'keplet';
  }

  mezoLabel(m: Mezo): string {
    const base = m.label || m.eazon;
    return this.isKotelezo(m) ? `${base} *` : base;
  }

  mezoSugo(m: Mezo): string {
    return this.layoutMezoMap[m.eazon]?.sugo || this.mezoLabel(m);
  }

  mezoHibak(m: Mezo): string[] {
    const errors: string[] = [];
    const meta = this.layoutMezoMap[m.eazon];
    const value = (m.ertek || '').trim();
    if (!meta) return errors;

    if (meta.kotelezo && !value) {
      errors.push('Kötelező mező.');
      return errors;
    }
    if (!value) return errors;

    if (meta.minHossz && value.length < meta.minHossz) {
      errors.push(`Minimum hossz: ${meta.minHossz}`);
    }
    if (meta.maxHossz && value.length > meta.maxHossz) {
      errors.push(`Maximum hossz: ${meta.maxHossz}`);
    }
    if (meta.format === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      errors.push('Dátum formátum: ÉÉÉÉ-HH-NN');
    }
    if (meta.mintazat) {
      try {
        const re = new RegExp(`^(?:${meta.mintazat})$`, 'u');
        if (!re.test(value)) errors.push('Formátumhiba (regex).');
      } catch {
        try {
          const re = new RegExp(`^(?:${meta.mintazat})$`);
          if (!re.test(value)) errors.push('Formátumhiba (regex).');
        } catch {
          // Invalid regex in config; skip pattern validation.
        }
      }
    }
    return errors;
  }

  mezoHibas(m: Mezo): boolean {
    return this.mezoHibak(m).length > 0;
  }

  private frissitSzamitottMezok() {
    for (const inst of this.lapInstanciak) {
      const byEazon = new Map<string, Mezo>(inst.mezok.map(m => [m.eazon, m]));
      for (let iter = 0; iter < 5; iter++) {
        let changed = false;
        for (const m of inst.mezok) {
          const meta = this.layoutMezoMap[m.eazon];
          if (!meta) continue;
          let uj = null as string | null;
          if (meta.szamitasTipus === 'masol' && meta.forrasEazon) {
            uj = byEazon.get(meta.forrasEazon)?.ertek || '';
          } else if (meta.szamitasTipus === 'keplet' && meta.keplet) {
            uj = meta.keplet.replace(/\{([^}]+)\}/g, (_, eazon: string) => byEazon.get(eazon)?.ertek || '');
          }
          if (uj !== null && m.ertek !== uj) {
            m.ertek = uj;
            changed = true;
          }
        }
        if (!changed) break;
      }
    }
  }

  isDropdown(m: Mezo): boolean {
    if (this.isCheckbox(m) || this.isKockas(m)) return false;
    return this.getDropdownOpciok(m).length > 0;
  }

  getDropdownOpciok(m: Mezo): DropdownOption[] {
    // Layout-based static options (from ENYK import) take priority
    const layoutEntry = this.layoutMezoMap[m.eazon];
    if (layoutEntry?.dropdownStatikus?.length) {
      return layoutEntry.dropdownStatikus.map(o => ({ value: o.ertek, label: o.nev }));
    }

    const formSzabaly = this.getDropdownOverrideSzabaly(m);
    if (formSzabaly) return formSzabaly.opciok;

    const cimke = m.label || '';
    const altalanos = this.altalanosDropdownok.find(s => s.pattern.test(cimke));
    return altalanos ? altalanos.opciok : [];
  }

  letoltPdf() {
    if (!this.kivalasztott) return;
    const esc = (s: string) => (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const azonosito = this.stripElotagok(this.kivalasztott.azonosito);
    const datum = new Date().toLocaleString('hu-HU');

    let sections = '';
    for (const inst of this.lapInstanciak) {
      const mezoMap = new Map<string, Mezo>(inst.mezok.map(m => [m.eazon, m]));
      const oldalak: any[] = this.layoutOldalakData.filter(
        (o: any) => !o.formId || o.formId === inst.azonosito
      );

      let instHtml = '';
      if (this.lapInstanciak.length > 1) {
        instHtml += `<h2 class="inst-title">${esc(inst.lapNev)}</h2>`;
      }

      if (oldalak.length === 0) {
        instHtml += '<table class="flat"><tbody>';
        for (const m of inst.mezok) {
          instHtml += `<tr><td class="lbl">${esc(m.label || m.eazon)}</td><td class="val">${esc(this.pdfErtek(m))}</td></tr>`;
        }
        instHtml += '</tbody></table>';
      } else {
        for (const oldal of oldalak) {
          if (oldalak.length > 1) {
            instHtml += `<h3 class="page-title">${esc(oldal.cim)}</h3>`;
          }
          for (const blokk of (oldal.blokkok || [])) {
            instHtml += '<div class="blokk">';
            if (blokk.cim) {
              instHtml += `<div class="blokk-cim">${esc(blokk.cim)}</div>`;
            }
            for (const sor of (blokk.sorok || [])) {
              const items: Array<{ m: Mezo | undefined; szeles: number; eazon: string }> =
                (sor.mezok || []).map((lm: any) => ({ m: mezoMap.get(lm.eazon), szeles: lm.szeles || 1, eazon: lm.eazon }));
              if (!items.length) continue;
              instHtml += '<div class="sor">';
              for (const item of items) {
                const label = item.m?.label || item.eazon;
                const value = item.m ? this.pdfErtek(item.m) : '';
                instHtml +=
                  `<div class="mezo" style="flex:${item.szeles}">` +
                  `<div class="lbl">${esc(label)}</div>` +
                  `<div class="val">${esc(value) || '&nbsp;'}</div>` +
                  `</div>`;
              }
              instHtml += '</div>';
            }
            instHtml += '</div>';
          }
        }
      }
      sections += `<section>${instHtml}</section>`;
    }

    const html = `<!DOCTYPE html>
<html lang="hu">
<head>
<meta charset="UTF-8">
<title>${esc(azonosito)}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#222;margin:0;padding:8mm}
h1{font-size:13pt;margin:0 0 1mm;color:#2c3e50}
.meta{font-size:8pt;color:#666;margin-bottom:5mm;border-bottom:1px solid #ddd;padding-bottom:2mm}
h2.inst-title{font-size:11pt;color:#2c3e50;border-bottom:1.5px solid #4c5d6f;margin:6mm 0 3mm;padding-bottom:1mm;page-break-before:always}
h2.inst-title:first-child{page-break-before:avoid}
h3.page-title{font-size:8.5pt;color:#888;margin:4mm 0 2mm;text-transform:uppercase;letter-spacing:.5px}
.blokk{margin-bottom:3mm}
.blokk-cim{background:#4c5d6f;color:#fff;font-size:8.5pt;font-weight:bold;padding:1.2mm 2mm;margin-bottom:1mm}
.sor{display:flex;gap:2mm;margin-bottom:1.5mm}
.mezo{flex:1;border:.5px solid #ccc;padding:1mm 2mm;min-width:0;page-break-inside:avoid}
.lbl{font-size:7.5pt;color:#555;font-weight:bold;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.val{font-size:9pt;min-height:5mm;word-break:break-word;padding-top:.5mm}
.flat{width:100%;border-collapse:collapse}
.flat td{padding:1.2mm 2mm;border-bottom:.3px solid #eee;font-size:9pt;vertical-align:top}
.flat td.lbl{width:48%;font-weight:bold;color:#444}
@media print{
  body{padding:0}
  @page{margin:10mm;size:A4 portrait}
  .blokk{page-break-inside:avoid}
}
</style>
</head>
<body>
<h1>${esc(azonosito)}</h1>
<div class="meta">Verzió: ${esc(this.kivalasztott?.verzio || '-')} &nbsp;|&nbsp; Készült: ${datum}</div>
${sections}
<script>window.addEventListener('load',function(){window.print()});<\/script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.addEventListener('unload', () => setTimeout(() => URL.revokeObjectURL(url), 2000));
    }
  }

  private getRenderTipus(m: Mezo): RenderTipus {
    // Layout-based override (e.g. from ENYK import) takes highest priority
    const layoutEntry = this.layoutMezoMap[m.eazon];
    if (layoutEntry?.tipus === 'checkbox') return 'checkbox';
    if (layoutEntry?.tipus === 'boxed') return 'boxed';

    const override = this.getOverrideSzabaly(m);
    if (override) return override.tipus;

    // Legacy ABEV forms used eazon type-code heuristics (F/H).
    // NAV_* XSD-based forms should rely on XSD-derived layout type instead.
    const azonosito = (this.kivalasztott?.azonosito || '').toUpperCase();
    const legacyKodHeurisztika = !azonosito.startsWith('NAV_');
    if (legacyKodHeurisztika) {
      if (m.tipuskod === 'F') return 'checkbox';
      if (m.tipuskod === 'H') return 'boxed';
    }

    const val = (m.ertek || '').trim();
    const lehetKockas = val.length >= 6 && val.length <= 16 && /^[0-9A-Za-z]+$/.test(val);
    return lehetKockas ? 'boxed' : 'text';
  }

  private getOverrideSzabaly(m: Mezo): { pattern: RegExp; tipus: RenderTipus; kockaDb?: number } | undefined {
    const azonosito = (this.kivalasztott?.azonosito || '').toUpperCase();
    const formKulcs = Object.keys(this.renderOverrideok).find(k => azonosito.includes(k.toUpperCase()));
    if (!formKulcs) return undefined;
    return this.renderOverrideok[formKulcs].find(s => s.pattern.test(m.eazon));
  }

  private getDropdownOverrideSzabaly(m: Mezo): { pattern: RegExp; opciok: DropdownOption[] } | undefined {
    const azonosito = (this.kivalasztott?.azonosito || '').toUpperCase();
    const formKulcs = Object.keys(this.dropdownOverrideok).find(k => azonosito.includes(k.toUpperCase()));
    if (!formKulcs) return undefined;
    return this.dropdownOverrideok[formKulcs].find(s => s.pattern.test(m.eazon));
  }

  private pdfErtek(m: Mezo): string {
    if (this.isCheckbox(m)) return m.ertek === 'X' ? 'Igen (X)' : 'Nem';
    return m.ertek || '';
  }

  private pdfFajlnev(azonosito: string): string {
    return `${azonosito}-${new Date().toISOString().slice(0, 10)}`.replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  private isDatumMezo(m: Mezo): boolean {
    const cimke = (m.label || '').toLowerCase();
    return cimke.includes('datum') || cimke.includes('dátum');
  }

  private isAdoazonositoMezo(m: Mezo): boolean {
    const cimke = (m.label || '').toLowerCase();
    return cimke.includes('adoazonosito') || cimke.includes('adószám') || cimke.includes('adoszam');
  }

  private getTipusKod(eazon: string): string {
    return (eazon && eazon.length > 6 ? eazon[6] : '').toUpperCase();
  }

  lapTorolheto(lapNev: string): boolean {
    const inst = this.lapInstanciak.find(l => l.lapNev === lapNev);
    if (!inst) return false;
    return this.lapInstanciak.filter(l => l.azonosito === inst.azonosito).length > 1;
  }

  buildXml(): string {
    if (!this.lapInstanciak.length) {
      // Fallback: simple regex replace on template
      const kitoltott: { [eazon: string]: string } = {};
      this.mezok.forEach(m => kitoltott[m.eazon] = m.ertek);
      return this.templateXml.replace(
        /<mezo eazon="([^"]+)">([^<]*)<\/mezo>/g,
        (_, eazon, oldVal) => `<mezo eazon="${eazon}">${kitoltott[eazon] !== undefined ? this.escapeXml(kitoltott[eazon]) : oldVal}</mezo>`
      );
    }

    // Preserve XML header (everything before first <nyomtatvany>)
    const headerMatch = this.templateXml.match(/^([\s\S]*?)<nyomtatvany[\s>]/);
    const header = headerMatch ? headerMatch[1] : '<?xml version="1.0" encoding="utf-8"?>\n<nyomtatvanyok xmlns="http://www.apeh.hu/abev/nyomtatvanyok/2005/01">\n';

    // Preserve footer (everything after last </nyomtatvany>)
    const lastClose = this.templateXml.lastIndexOf('</nyomtatvany>');
    const footer = lastClose >= 0 ? this.templateXml.substring(lastClose + '</nyomtatvany>'.length) : '\n</nyomtatvanyok>';

    const nyomtBlocks = this.lapInstanciak.map(inst => {
      const mezokXml = inst.mezok.map(m =>
        `      <mezo eazon="${m.eazon}">${this.escapeXml(m.ertek || '')}</mezo>`
      ).join('\n');
      return `  <nyomtatvany>\n    <nyomtatvanyinformacio>${inst.infoBlock}</nyomtatvanyinformacio>\n    <mezok>\n${mezokXml}\n    </mezok>\n  </nyomtatvany>`;
    }).join('\n');

    return header + nyomtBlocks + footer;
  }

  private escapeXml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  letolt() {
    const xml = this.buildXml();
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (this.kivalasztott!.azonosito || 'nyomtatvany') + '.xml';
    a.click();
    URL.revokeObjectURL(url);
  }
}
